import { XHR_DONE, XHR_STATUS_ABORTED, XHR_UNSENT } from '@fullstory/shared/const';
import { assertExhaustive } from '@fullstory/typeutils';
import { getGRPCSafeMessage } from './grpc-safe-message';

const DefaultBatchId = '';

// Extract JSON from an XMLHttpRequest, where "" is valid and yields {}.
// returns null if fails to Parse.
export function HandleJson(xhr: XMLHttpRequest): { rsp: any; safeMessage: string | null } {
  // Double check the response payload was in fact JSON.
  const contentType = xhr.getResponseHeader('Content-Type');
  const safeMessage = getSafeMessage(xhr);
  if (contentType && contentType.indexOf('application/json') >= 0) {
    let data = xhr.responseText;
    try {
      // Special case -- we want to be able to treat '200 with empty response' as 'success',
      // so we fix up empty data to make sure it parses as an empty JSON object.
      if (!data) {
        data = '{}';
      }
      return {
        rsp: JSON.parse(data),
        safeMessage,
      };
    } catch (e) {
      // invalid json, we'll deliver null.
      console.log(`Error parsing json: ${data}`);
      console.log(e);
      return {
        rsp: null,
        safeMessage,
      };
    }
  } else {
    return {
      rsp: xhr.responseText,
      safeMessage,
    }; // Give up. Just return the text.
  }
}

function HandleBinary(xhr: XMLHttpRequest): { rsp: any; safeMessage: string | null } {
  return { rsp: xhr.response, safeMessage: getSafeMessage(xhr) };
}

function getSafeMessage(xhr: XMLHttpRequest): string | null {
  return getGRPCSafeMessage(
    xhr.getResponseHeader('x-grpc-details') ?? '',
    xhr.getResponseHeader('x-grpc-status') ?? '',
    () => console.error('Error parsing SafeMessage from header'),
  );
}

export interface Response<T = any> {
  status: number;
  body: T;
}

// fetch wraps the xhr request for GET (no data provided), or POST JSON requests
//
// TODO(nishanth): make this take in a Xhr.Request instead. This way, callers
// can determine whether they decide on the request's Content-Type and method
// instead of being restricted to PostJson and GetJson.
// TODO(sethf): fetchRequest() provides an Xhr.Request API, so to complete the
// TODO above, we'd just need to switch all callers over to fetchRequest() and
// then rename fetchRequest() to fetch().
export function fetch(
  xhr: NetworkController,
  url: string,
  data?: any,
  id?: string,
): Promise<Response> {
  return fetchRequest(data ? xhr.PostJson(url) : xhr.GetJson(url), data, id);
}

// fetchRequest() is like fetch(), but it accepts an Xhr.Request.
export function fetchRequest<T = any>(req: Request, data?: any, id?: string): Promise<Response<T>> {
  return new Promise<Response>((resolve, reject) => {
    if (id) {
      req.LastOneWins(id);
    }
    req
      .Fail((status: number, rsp: any, safeMessage: string | null, _xhr?: XMLHttpRequest): void => {
        // TODO(sethf): The Response we're passing to reject() here will show up
        // in a catch() block as if it were an exception, which seems likely to be
        // a little surprising.
        reject({ status, body: rsp, safeMessage });
      })
      .Success((status: number, rsp: T, _xhr?: XMLHttpRequest): void => {
        resolve({ status, body: rsp });
      })
      .Send(data);
  });
}

export interface RequestFailure {
  status: number;
  response: any;
  safeMessage: string;
}
export const isRequestFailure = (err: any): err is RequestFailure =>
  // response can be undefined, only validate for the number
  typeof err?.status === 'number';

export type PostJSON<I = any, O = any> = (payload: I, endpoint: string) => Promise<O>;
export function xhrPost<I = any, O = any>(
  xhr: NetworkController,
  settings?: RequestBuilderSettings,
): PostJSON<I, O> {
  return (payload: I, endpoint: string) =>
    buildXhrPost<I, O>(xhr, endpoint, payload, settings).promise();
}

export function buildXhrPost<I, O>(
  xhr: NetworkController,
  endpoint: string,
  payload: I,
  settings?: RequestBuilderSettings,
): {
  promise: () => Promise<O>;
  request: Request;
} {
  const request = xhr.PostJson(endpoint);
  if (settings) applyRequestBuilderSettings(request, settings);
  return {
    promise: () =>
      new Promise((resolve, reject) => {
        request
          .BuildRequest()
          .Success((_, response) => resolve(response))
          .Fail((status, response, safeMessage) =>
            reject(<RequestFailure>{ status, response, safeMessage }),
          )
          .Send(payload);
      }),
    request,
  };
}

export type RetryEnvelope<T> =
  | {
      done: true;
      envelope: null;
    }
  | {
      done: false;
      envelope: T;
    };

/**
 * Run and retry `promise()` upto `retryCount` times untils the promise is resolved. The promise
 * returned from retryPromise is resolved whenever `promise()` is resolved or rejected after
 * running out of retries
 *
 * @param promise A function that will return the promise which will be retried
 * @param retryCount The maximum number of retries
 */
export async function retryPromise<T>(promise: () => Promise<T>, retryCount: number): Promise<T> {
  if (retryCount < 0) {
    throw new Error('retry expects a number >= 0.');
  }

  let error: any;
  for (let i = 0; i <= retryCount; i++) {
    try {
      return await promise();
    } catch (err) {
      error = err;
    }
  }
  throw error;
}

// retry wraps any function in a higher order function that exposes a retryCb method that the consumer should invoke to continue the recursive process.
// retryCb method will re-run whatever is wrapped by retry and also exposes the next recursive call's return value, making it easy to chain promises.
// check the failed state of the returned envelope from retryCb to determine if the remaining retries reaches 0.
export function retry<T>(requestFn: (retryCb: () => RetryEnvelope<T>) => T, remaining: number): T {
  if (remaining < 0) {
    throw new Error('retry expects a number >= 0.');
  }
  return _retry(requestFn, remaining);
}

function _retry<T>(requestFn: (retryCb: () => RetryEnvelope<T>) => T, remaining: number): T {
  let _retryCache: RetryEnvelope<T> | null = null;
  const _retryCb = (): RetryEnvelope<T> => {
    if (_retryCache) {
      return _retryCache;
    }
    if (remaining <= 0) {
      _retryCache = {
        envelope: null,
        done: true,
      };
    } else {
      _retryCache = {
        envelope: _retry(requestFn, remaining - 1),
        done: false,
      };
    }
    return _retryCache;
  };
  return requestFn(_retryCb);
}

type AbortOptions = {
  excludeTags?: string[];
};

type CancelPendingOptions = AbortOptions;

export interface PreparedRequest<I = any, O = any> {
  readonly url: string;
  Success(f: (status: number, rsp: O, xhr?: XMLHttpRequest) => void): PreparedRequest<I, O>;
  Fail(
    f: (status: number, rsp: O, safeMessage: string | null, xhr?: XMLHttpRequest) => void,
  ): PreparedRequest<I, O>;
  Send(data?: I): void;
  Abort(): void;
}

export enum RequestType {
  Get,
  GetBinary,
  Delete,
  Post,
  PostForm,
  GetJson,
  PostJson,
}

export interface BatchOptions {
  maxOutgoingRequests: number;
  id: string;
}

export interface RequestSettings extends RequestBuilderSettings {
  requestType: RequestType;
  url: string;
  params?: Record<string, string>;
}

export interface RequestBuilderSettings {
  batchOptions?: BatchOptions;
  excluded?: boolean;
  notification?: boolean;
  headers?: { [name: string]: string };
}

export function requestSettingsBuilder(xhr: NetworkController, settings: RequestSettings): Request {
  switch (settings.requestType) {
    case RequestType.Get:
      return xhr.Get(settings.url, settings.params);
    case RequestType.GetBinary:
      return xhr.GetBinary(settings.url, settings.params);
    case RequestType.Delete:
      return xhr.Delete(settings.url, settings.params);
    case RequestType.Post:
      return xhr.Post(settings.url, settings.params);
    case RequestType.PostForm:
      return xhr.PostForm(settings.url, settings.params);
    case RequestType.GetJson:
      return xhr.GetJson(settings.url, settings.params);
    case RequestType.PostJson:
      return xhr.PostJson(settings.url, settings.params);
    default:
      assertExhaustive(settings.requestType, 'unexpected request type');
  }
}

export function applyRequestBuilderSettings(
  sourceBuilder: Request,
  settings: RequestBuilderSettings,
): Request {
  let builder = sourceBuilder;
  if (settings.headers) builder = builder.SetHeaders(settings.headers);
  if (settings.batchOptions)
    builder = builder.Batch(settings.batchOptions.maxOutgoingRequests, settings.batchOptions.id);
  if (settings.excluded != null) builder = builder.SetExcluded(settings.excluded);
  if (settings.notification != null) builder = builder.TriggerNotification(settings.notification);
  return builder;
}

export interface RequestQueueController {
  Abort(kind: string, opts: AbortOptions): void;
  CancelPending(kind: string, opts: CancelPendingOptions): void;
}

// Simple API for issuing network requests. Exposes hooks for setting custom headers, and for getting side-band notifications
// whenever certain status codes are encountered.
export class NetworkController {
  _customHeaders: { [name: string]: string } = {};
  _notifications: { [name: string]: (xhr: XMLHttpRequest) => boolean } = {};
  _batchesById: { [id: string]: RequestQueue } = {};
  _lastOneWinsIds: { [kind: string]: number } = {};

  _outstandingReqsChangedHandler?: (xhr: NetworkController) => void;
  _outstandingReqs: number = 0;

  constructor() {}

  // If any request made via this controller encounters one of these status codes. Notify the callback.
  public SetNotifications(notifications: { [name: string]: (xhr: XMLHttpRequest) => boolean }) {
    this._notifications = notifications;
  }

  public SetReqsChangedHandler(reqsChangedHandler: (xhr: NetworkController) => void) {
    this._outstandingReqsChangedHandler = reqsChangedHandler;
  }

  public GetOutstandingReqs(): number {
    return this._outstandingReqs;
  }

  public Get<I = any, O = any>(url: string, params?: Record<string, string>): Request<I, O> {
    return this.finalize(new Request<I, O>(this, 'GET', url, params));
  }

  public GetBinary<I = any, O = any>(url: string, params?: Record<string, string>): Request<I, O> {
    return this.finalize(
      new Request<I, O>(this, 'GET', url, params, 'arraybuffer'),
    ).SetResponseAdapter(HandleBinary);
  }

  public Delete<I = any, O = any>(url: string, params?: Record<string, string>): Request<I, O> {
    return this.finalize(new Request<I, O>(this, 'DELETE', url, params));
  }

  public Post<I = any, O = any>(
    url: string,
    params?: Record<string, string>,
    contentType?: string,
  ): Request<I, O> {
    const req = new Request<I, O>(this, 'POST', url, params);
    if (contentType) {
      req.SetHeader('Content-Type', contentType);
    }
    return this.finalize(req);
  }

  public PostForm<I = any, O = any>(url: string, params?: Record<string, string>): Request<I, O> {
    return this.Post<I, O>(url, params, 'application/x-www-form-urlencoded');
  }

  public GetJson<I = any, O = any>(url: string, params?: Record<string, string>): Request<I, O> {
    return this.Get<I, O>(url, params).SetResponseAdapter(HandleJson);
  }

  public PostJson<I = any, O = any>(url: string, params?: Record<string, string>): Request<I, O> {
    return this.Post<I, O>(url, params, 'application/json').SetResponseAdapter(HandleJson);
  }

  public Abort(kind: string, { excludeTags = [] }: AbortOptions = {}) {
    this.queue(kind).abort({ excludeTags });
  }

  public CancelPending(kind: string, { excludeTags = [] }: CancelPendingOptions = {}) {
    this.queue(kind).cancelPending({ excludeTags });
  }

  // Ensure that custom headers and notification hooks are installed on each new Request.
  finalize(req: Request): Request {
    return req.SetNotifications(this._notifications).SetHeaders(this._customHeaders);
  }

  // Allocate a lastOneWins Id for the supplied kind.
  newId(lastOneWinsKind: string): number {
    if (!this._lastOneWinsIds[lastOneWinsKind]) {
      this._lastOneWinsIds[lastOneWinsKind] = 0;
    }
    const nextId = this._lastOneWinsIds[lastOneWinsKind] + 1; // The smallest ID that can be given out is 1.
    this._lastOneWinsIds[lastOneWinsKind] = nextId;
    return nextId;
  }

  tipId(lastOneWinsKind: string | undefined): number {
    if (lastOneWinsKind === undefined) {
      return 0;
    }
    const id = this._lastOneWinsIds[lastOneWinsKind];
    return id ? id : 0;
  }

  queue(batchId: string): RequestQueue {
    if (!this._batchesById[batchId]) {
      this._batchesById[batchId] = new RequestQueue();
    }
    return this._batchesById[batchId];
  }

  onRequestSend(req: Request) {
    if (this._outstandingReqsChangedHandler && !req.GetExcluded()) {
      this._outstandingReqs++;
      this._outstandingReqsChangedHandler(this);
    }
  }

  onRequestDone(req: Request) {
    if (this._outstandingReqsChangedHandler && !req.GetExcluded()) {
      this._outstandingReqs--;
      this._outstandingReqsChangedHandler(this);
    }
  }
}

/**
 * Splits a URL path into its path component and its query params, which are returned as an object
 * holding key-value pairs
 *
 * @param url A URL path that may also include a query string
 */
const parseUrl = (url: string): [string, Record<string, string>] => {
  const [path, queryString] = url.split('?');

  if (!queryString) return [path, {}];

  return [
    path,
    queryString
      .split('&')
      .map(pair => pair.split('=').map(decodeURIComponent))
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {}),
  ];
};

/**
 * Builds a query string from a set of key-value pairs
 *
 * @param params Key-value pairs that are used as query parameters
 */
const buildQueryString = (params: Record<string, string>): string => {
  if (!params || Object.keys(params).length == 0) return '';

  const queryString = Object.entries(params)
    .map(pair => pair.map(encodeURIComponent).join('='))
    .join('&');

  return `?${queryString}`;
};

/**
 * Builds a URL path from an existing path and a set of query params
 *
 * @param url A URL path string that might also include a query string
 * @param params Key-value pairs that will be added to the query string of the resulting URL path.
 * If a key already exists in the specified URL path its value will be replaced by the value in this
 * object.
 */
const getUrlWithParams = (url: string, params: Record<string, string> = {}): string => {
  const [path, existingParams] = parseUrl(url);
  const queryString = buildQueryString({ ...existingParams, ...params });
  return `${path}${queryString}`;
};

// Wrapper for XMLHttpRequest that exposes a builder-like API for constructing and sending the request.
// TODO(craigmccown): Separate request building logic from request sending and response handling logic.
// The interfaces that `Request` and `NetworkController` currently implement mimic the correct types,
// but the implementation remains the same.
export class Request<I = any, O = any> implements PreparedRequest {
  _ctrl: NetworkController;
  _xhr: XMLHttpRequest;
  _rspAdapter: (xhr: XMLHttpRequest) => { rsp: any; safeMessage: string | null };
  _success?: (status: number, rsp: O, xhr?: XMLHttpRequest) => void;
  _fail?: (status: number, rsp: any, safeMessage: string | null, xhr?: XMLHttpRequest) => void;
  _excludeFromOutstandingReqs: boolean = false; // true if this request should not trigger the outstanding reqs change handler (i.e., the loading notification)

  private _beforeSend?: () => Promise<void>;

  // For hooking side band notifications on requests without having to
  _notifications?: { [name: string]: (xhr: XMLHttpRequest) => boolean };

  _batchId = DefaultBatchId;
  _lastOneWinsKind?: string;
  _lastOneWinsId?: number;
  _tag?: string;

  private _data: any;
  private readonly _method: string;
  private _headers: { [name: string]: string } = {};
  readonly url: string;
  private _authFailFunc?: () => void;

  constructor(
    ctrl: NetworkController,
    method: string,
    url: string,
    params: Record<string, string> = {},
    responseType?: XMLHttpRequestResponseType,
  ) {
    this._ctrl = ctrl;
    this.url = getUrlWithParams(url, params);
    this._method = method;
    this._rspAdapter = xhr => ({ rsp: xhr.responseText, safeMessage: null }); // Default
    this._xhr = new XMLHttpRequest();
    if (responseType) {
      this._xhr.responseType = responseType;
    }
    this._xhr.open(method, this.url, true);
    this._xhr.onreadystatechange = () => {
      this._handleReadyStateChange();
    };
  }

  // Resets the request to its initial state, and reapplies any custom headers.
  private reset() {
    this._xhr.open(this._method, this.url, true);

    // Headers must be reset after open() is called.
    for (const h in this._headers) {
      this._xhr.setRequestHeader(h, this._headers[h]);
    }
  }

  // Retries the request with the data previously supplied via `Send()`.
  public Retry() {
    this.reset();
    this.Send(this._data);
  }

  public SetResponseAdapter(
    adapter: (xhr: XMLHttpRequest) => { rsp: any; safeMessage: string | null },
  ): Request<I, O> {
    this._rspAdapter = adapter;
    return this;
  }

  BeforeSend(hook: () => Promise<void>): void {
    this._beforeSend = hook;
  }

  public SetNotifications(notifications: {
    [name: string]: (xhr: XMLHttpRequest) => boolean;
  }): Request<I, O> {
    this._notifications = notifications;
    return this;
  }

  public Success(f: (status: number, rsp: O, xhr?: XMLHttpRequest) => void): Request<I, O> {
    this._success = f;
    return this;
  }

  public Fail(
    f: (status: number, rsp: any, safeMessage: string | null, xhr?: XMLHttpRequest) => void,
  ): Request<I, O> {
    this._fail = f;
    return this;
  }

  public SetHeaders(customHeaders?: { [name: string]: string }): Request<I, O> {
    // TODO(jaime): Consider allowing control of setting custom headers for cross domain requests.
    if (!customHeaders || !isSameOrigin(this.url)) {
      return this;
    }

    for (const h in customHeaders) {
      this.SetHeader(h, customHeaders[h]);
    }

    return this;
  }

  // Returns the value of the specified response header.
  public GetResponseHeader(name: string): string | null {
    return this._xhr.getResponseHeader(name);
  }

  public WithCredentials(): Request<I, O> {
    this._xhr.withCredentials = true;
    return this;
  }

  public SetHeader(name: string, val: string): Request<I, O> {
    this._headers[name] = val;
    this._xhr.setRequestHeader(name, val);
    return this;
  }

  public SetExcluded(exclude: boolean): Request<I, O> {
    this._excludeFromOutstandingReqs = exclude;
    return this;
  }

  public GetExcluded(): boolean {
    return this._excludeFromOutstandingReqs;
  }

  public TriggerNotification(trigger: boolean): Request<I, O> {
    this.SetExcluded(!trigger); // trigger the notification => don't exclude it
    return this;
  }

  public Send(data?: I) {
    this._data = data;
    this._ctrl.queue(this._batchId).do(this, data);
  }

  // Enables "last one wins" mode. Only the last one of the requests of this kind,
  // that also participate in "last one wins" will have the result handler execute.
  public LastOneWins(kind: string): Request<I, O> {
    this._lastOneWinsKind = kind;
    this._lastOneWinsId = this._ctrl.newId(kind);
    return this;
  }

  public BuildRequest(): Request<I, O> {
    return this;
  }

  // Makes all requests of this kind not have more than the specified number of outgoing requests.
  // maxOutgoingRequests of 0 means "no batching" and no request queuing will take place.
  public Batch(maxOutgoingRequests: number, id: string): Request<I, O> {
    this._batchId = id;
    this._ctrl.queue(id).setBatchSize(maxOutgoingRequests);
    return this;
  }

  public BatchId(): string {
    return this._batchId;
  }

  // Kills the request even if it is in flight.
  public Abort() {
    this._ctrl.queue(this._batchId).abortSingle(this);
  }

  public SetTag(tag: string): Request<I, O> {
    this._tag = tag;
    return this;
  }

  /**
   * If the request returns an HTTP 401 status code, reload the page to re-authenticate.
   * @param url The url to reload. Defaults to the current url at invocation time.
   * There are cases where a different redirect url is desired.
   */
  public WithAuthReload(url: string = window.location.href): Request<I, O> {
    this._authFailFunc = () => {
      window.location.assign(url);
    };
    return this;
  }

  /**
   * If the request returns an HTTP 401 status code, creates a hidden iframe to relauth
   * in the background.
   */
  public WithAuthFailFunc(f: (r: Request<I, O>) => void): Request<I, O> {
    this._authFailFunc = () => {
      f(this);
    };
    return this;
  }

  _sendNow(data?: any) {
    if (this._xhr.readyState !== XMLHttpRequest.OPENED) return;

    // Set CSRF token if present in client-side JS cookie. We do this when the request is sent instead of
    // when we populate the headers since sometimes requests sit in a queue when we are doing batched dispatch.
    // And the CSRF token can be changed between then at it actually being sent.
    if (isSameOrigin(this.url)) {
      const csrftoken = readCsrftoken();
      if (csrftoken) {
        this.SetHeader('Csrftoken', csrftoken);
      }
    }

    (async () => {
      // Provide any possibly async steps to finalize the request.
      if (this._beforeSend) {
        await this._beforeSend();
      }

      this._ctrl.onRequestSend(this);

      if (typeof data == 'string') {
        this._xhr.send(data);
      } else {
        // TODO(jaime): If we ever have need for binary XHR or something we should inspect Content-Type
        // and make serialization adjustments using the value of that. For now, assume "not string" means JSON.
        this._xhr.send(JSON.stringify(data));
      }
    })();
  }

  _handleReadyStateChange() {
    const xhr = this._xhr;
    if (xhr.readyState != XHR_DONE) {
      if (xhr.readyState == XHR_UNSENT) {
        // if the xhr was aborted, its readyState goes back to Unsent.
        // but we still want to tell the controller that the aborted request should be considered done.
        // this handler is never called before the req is sent, so this should be safe to do.
        this._ctrl.onRequestDone(this);
      }
      return;
    }

    // Notify ctrl
    this._ctrl.onRequestDone(this);

    // Remove this from the in-flight queue.
    this._ctrl.queue(this._batchId).completed(this);

    // TODO(jaime): lastOneWins should probably also call the failure callbacks for
    // any request that was suppressed. Consider either eagerly aborting them, or calling the failure callback here.
    // But if we do that, we need to audit all LastOneWins requests so that they gracefully handle the abort status code.
    if (this._lastOneWinsId && this._ctrl.tipId(this._lastOneWinsKind) != this._lastOneWinsId) {
      // Last one wins is enabled. Check ID against tip to see if we are the last request to go out.
      return;
    }

    if (xhr.status === 401 && this._authFailFunc) {
      this._authFailFunc();
      return;
    }

    // Notify side-band observers.
    if (this._notifications) {
      for (const status in this._notifications) {
        if (`${xhr.status}` == status) {
          if (this._notifications[status](xhr)) {
            // Bail because the sideband notification said it needs to steal flow control.
            return;
          } // else continue.
        }
      }
    }

    // TODO(jaime): If the rsp fails to JSON.parse, but returns a 2XX, should we funnel to the fail handler?
    // That seems.... like a scary condition. For now let the success handler eat a null.
    const { rsp, safeMessage } = this._rspAdapter(xhr);
    if (xhr.status >= 200 && xhr.status <= 299) {
      if (this._success) {
        this._success(xhr.status, rsp, xhr);
      }
    } else if (this._fail) {
      this._fail(xhr.status, rsp, safeMessage, xhr);
    }
  }
}

// TODO(jaime): Make this stricter. For example, double check protocol relative URLs don't get blessed as same origin.
// Very hacky same origin test.
const _myDomain = `${window.location.protocol}//${window.location.host}`;
export function isSameOrigin(url: string) {
  return url.indexOf('/') == 0 || url.indexOf(_myDomain) == 0;
}

// Read CSRF token from client-side JS cookie.
export function readCsrftoken(): string | null {
  const cookies = document['cookie'].split(';');
  for (let i = 0; i < cookies.length; i++) {
    const part = cookies[i].replace(/^\s+|\s+$/g, ''); // Yuck: Old IEs don't support trim().
    if (part.indexOf('fs_csrftoken') == 0) {
      return part.substring('fs_csrftoken='.length, part.length);
    }
  }
  return null;
}

export interface PendingRequest {
  req: Request;
  data?: any;
}

// Manages execution of all batched requests of a certain Kind.
export class RequestQueue {
  _inFlight = <Request[]>[];
  _queued = <PendingRequest[]>[];
  _maxOutgoing = 0;

  constructor() {}

  size() {
    return this._inFlight.length + this._queued.length;
  }

  abort({ excludeTags = [] }: AbortOptions = {}) {
    const reqsAndTags = this._inFlight.map(req => ({
      tag: req._tag,
      xhr: req._xhr,
    }));
    reqsAndTags.forEach(req => {
      if (req.tag !== undefined && excludeTags.includes(req.tag)) {
        // if request tag is present in excludeTags, do not abort.
        return;
      }
      req.xhr.abort();
    });
    this._inFlight = [];
    this.cancelPending({ excludeTags });
  }

  cancelPending({ excludeTags = [] }: CancelPendingOptions = {}) {
    this._queued.forEach(pending => {
      if (pending.req._tag !== undefined && excludeTags.includes(pending.req._tag)) {
        // if request tag is present in excludeTags, do not cancel.
        return;
      }
      this._fireAbortFail(pending.req);
    });
    this._queued = [];
  }

  abortSingle(req: Request) {
    if (this._removeInFlight(req)) {
      req._xhr.abort();
    } else if (this.removePending(req)) {
      this._fireAbortFail(req);
    }
  }

  do(req: Request, data?: any) {
    this._batchedDo(req, data);
  }

  completed(req: Request) {
    this._removeInFlight(req);
    this._pumpPending();
  }

  setBatchSize(maxOutgoing: number) {
    this._maxOutgoing = maxOutgoing;
  }

  removePending(req: Request): boolean {
    for (let i = 0; i < this._queued.length; i++) {
      if (this._queued[i].req == req) {
        this._queued.splice(i, 1);
        return true;
      }
    }
    return false;
  }

  _removeInFlight(req: Request): boolean {
    for (let i = 0; i < this._inFlight.length; i++) {
      if (this._inFlight[i] == req) {
        this._inFlight.splice(i, 1);
        return true;
      }
    }
    return false;
  }

  _send(req: Request, data?: any) {
    this._inFlight.push(req);
    req._sendNow(data);
  }

  _batchedDo(req: Request, data?: any) {
    if (this._maxOutgoing == 0) {
      // No batching.
      this._send(req, data);
    } else {
      // Batched send.
      this._queued.push(<PendingRequest>{
        req,
        data,
      });
      this._pumpPending();
    }
  }

  _pumpPending() {
    const ableToPump = this._maxOutgoing - this._inFlight.length;
    for (let i = 0; i < ableToPump; i++) {
      const toSend = this._queued.shift();
      if (toSend) {
        this._send(toSend.req, toSend.data);
      }
    }
  }

  // Call the failure callback in scenarios where the XHR callback won't be called.
  _fireAbortFail(req: Request) {
    // TODO(jaime): What if they never registered a failure callback, but wrap in a promise? That's a leak too :(.
    if (req._fail) {
      req._fail(XHR_STATUS_ABORTED, null, null, req._xhr);
    }
  }
}
